import * as React from "react";

import { useContext } from "react";
import {
  useTheme,
  FormControlLabel,
  Checkbox,
  TextField,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  DatePicker,
  DateTimePicker,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import {
  usePickerLayout,
  pickersLayoutClasses,
  PickersLayoutRoot,
  PickersLayoutContentWrapper,
} from "@mui/x-date-pickers/PickersLayout";
import { AppContext } from "../../contextProviders/AppContext";
import {
  add,
  addHours,
  getMinutes,
  isValid,
  roundToNearestMinutes,
  set,
} from "date-fns";
import { translation } from "../Dictionary";
import { Box } from "@mui/system";
import { useRef } from "react";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import DatePickerMobileSingleDay from "./DatePickerMobileSingleDay";

export default function DatePickerPredetermined({
  getMinimumDate,
  localeText,
  startTimeLabel,
  returnTimeLabel,
  companyOpenHours,
  companyOpenHoursReturn,
  openDateRangeMobile,
  setOpenDateRangeMobile,
  startDateString,
}) {
  const {
    selectedCompany,
    language,
    reservationStart,
    setSelectedDateRange,
    isDisabled,
    selectedDateRange,
    selectedPicker,
    setSelectedPicker,
    customDatePickerType,
    mobileViewPort,
  } = useContext(AppContext);
  const containerRef = useRef();
  //

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleCloseCalendar = () => {
    if (!reservationStart) {
      setSelectedPicker("startTime");
    } else {
      setSelectedPicker(null);
    }
  };
  const handleChangeDate = (newValue) => {
    // single day datepicker doesnt return an array, and this mixed up
    // logic used with daterange pickers
    const start = set(newValue, { hours: 8 });
    const end = add(start, { hours: 5 });
    const dateToArray = [start, end];
    setSelectedDateRange(dateToArray);
  };

  const deliveryBufferTime = React.useMemo(() => {
    if (!selectedCompany?.deliveryBufferHours) {
      return set(addHours(new Date(), 1), { minutes: 0 });
    }
    // TODO, in some cases, the buffer time isnt correct when changing companies, (from buffer of 4 to buffer of 24)
    return set(addHours(new Date(), selectedCompany?.deliveryBufferHours + 1), {
      minutes: getMinutes(roundToNearestMinutes(new Date(), { nearestTo: 15 })),
    });
  }, [selectedCompany?.deliveryBufferHours]);

  if (mobileViewPort) {
    return (
      <DatePickerMobileSingleDay
        selectedPicker={selectedPicker}
        setSelectedPicker={setSelectedPicker}
        companyOpenHours={companyOpenHours}
        companyOpenHoursReturn={companyOpenHoursReturn}
        getMinimumDate={getMinimumDate}
        openDateRangeMobile={openDateRangeMobile}
        returnTimeLabel={returnTimeLabel}
        setOpenDateRangeMobile={setOpenDateRangeMobile}
        startDateString={startDateString}
        VUOKRAAPAKU_SPECIAL_COMPANY={false}
        handleChangeDate={handleChangeDate}
        deliveryBufferTime={deliveryBufferTime}
        startTimeLabel={startTimeLabel}
        layout={CustomLayout}
        actionBar={ActionList}
        textField={CustomTextField}
        customDatePickerType={customDatePickerType}
      />
    );
  }
  // false = single day picker, true = daterange picker
  if (!customDatePickerType) {
    return (
      <Box ref={containerRef} width={"150px"}>
        <DatePicker
          minDate={getMinimumDate()}
          shouldDisableDate={isDisabled}
          label={translation.get(language, "Date label")}
          disabled={!selectedCompany}
          disableMaskedInput
          PaperProps={{
            square: true,
            sx: {
              borderRadius: "17px",
              marginTop: "12px",
            },
          }}
          onOpen={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedPicker("calendar");
          }}
          open={selectedPicker === "calendar"}
          onClose={() => handleCloseCalendar}
          disablePast
          value={selectedDateRange[0]}
          onChange={(newValue) => {
            // newValue = date instead of an array [date, date],
            //because DatePicker is used instead of DaterangePicker

            handleChangeDate(newValue);
            setSelectedPicker("startTime");
          }}
          slots={{
            layout: CustomLayout,
            actionBar: ActionList,
            textField: CustomTextField,
          }}
          PopperProps={{
            placement: "bottom-end",
            anchorEl: anchorEl,
          }}
        />
      </Box>
    );
  }

  return (
    <DateRangePicker
      slotProps={{
        textField: ({ position }) => ({}),
        fieldSeparator: {
          children: "",
        },
      }}
      disablePast
      onOpen={() => setSelectedPicker("calendar")}
      shouldDisableDate={isDisabled}
      open={selectedPicker === "calendar"}
      onClose={() => setSelectedPicker("startTime")}
      disableOpenPicker={!selectedCompany}
      value={selectedDateRange}
      localeText={localeText}
      onChange={(newValue) => {
        console.log("newValue", newValue);
        const start = isValid(newValue[0])
          ? set(newValue[0], { hours: 8 })
          : null;
        const end = isValid(newValue[1])
          ? set(newValue[1], { hours: 13 })
          : null;
        setSelectedDateRange([start, end]);
      }}
      slots={{
        layout: CustomLayout,
        actionBar: ActionList,
        textField: CustomTextField,
      }}
    />
  );
}

const CustomTextField = (props) => {
  const theme = useTheme();
  const { setSelectedPicker } = useContext(AppContext);
  return (
    <Box
      onClick={() => setSelectedPicker("calendar")}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <TextField
        {...props}
        InputProps={{
          ...props.InputProps,
          disableUnderline: true,
          style: {
            backgroundColor: "transparent",
            border: "none",
          },
          endAdornment: null,
        }}
        inputProps={{
          ...props.inputProps,
          readOnly: true,
          style: { cursor: "pointer" },
          placeholder: "",
        }}
        sx={{
          input: {
            color: theme.palette.primary.mainText,
            borderRadius: "10px",
            cursor: "pointer",
            transition: "150ms ease-in",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.05)",
              boxShadow: theme.shadows[2],
            },
          },
          maxWidth: "120px",
        }}
        variant="filled"
      />
    </Box>
  );
};

function CustomLayout(props) {
  const { toolbar, tabs, content, actionBar } = usePickerLayout(props);

  return (
    <PickersLayoutRoot
      ownerState={props}
      sx={{
        overflow: "auto",
        [`.${pickersLayoutClasses.actionBar}`]: {
          gridColumn: 1,
          gridRow: 2,
        },
        [`.${pickersLayoutClasses.toolbar}`]: {
          gridColumn: 2,
          gridRow: 1,
        },
      }}
    >
      {toolbar}

      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
      >
        {tabs}
        {actionBar}

        {content}
      </PickersLayoutContentWrapper>
    </PickersLayoutRoot>
  );
}

function ActionList() {
  const {
    customDatePickerType,
    setCustomDatePickerType,
    language,
    setSelectedDateRange,
  } = useContext(AppContext);

  return (
    <Box display={"flex"} justifyContent={"center"} padding={"10px"}>
      <FormControl>
        <FormLabel component="legend">{groupLabel[language]}</FormLabel>
        <RadioGroup row>
          <FormControlLabel
            value="single"
            control={
              <Radio
                value="single"
                onClick={() => {
                  setCustomDatePickerType(false);
                  setSelectedDateRange([null, null]);
                }}
                checked={!customDatePickerType}
              />
            }
            label={singleDayLabel[language]}
          />
          <FormControlLabel
            value="range"
            control={
              <Radio
                value="range"
                onClick={() => {
                  setCustomDatePickerType(true);
                  setSelectedDateRange([null, null]);
                }}
                checked={customDatePickerType}
              />
            }
            label={rangeLabel[language]}
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );
}

const groupLabel = {
  fi: "Varauksen kesto",
  se: "Bokningens längd",
  en: "Reservation duration",
};

const singleDayLabel = {
  fi: "5h varaus",
  en: "5h reservation",
  se: "5h bokning",
};

const rangeLabel = {
  fi: "Yli 5h varaus",
  en: "Over 5h reservation",
  se: "Över 5h bokning",
};
